import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Button, CircularProgress } from '@mui/material';
import ToastMessage from '../../../components/ToastMessage';
import { SnackbarSeverityType } from '../../../enums';
import { useToast } from '../../AdminUsers/utils/useToast';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../../components/DatePicker';
import DropdownSelect from '../../../components/DropdownSelect';
import MultiSelectWithAll from '../../../components/MultiSelectWithAll';
import {
  academicYears,
  defaultAcademicYear
} from '../../../constants/academicYears';
import { ReportFilterProps } from '../types';
import { PaperWrapper } from '../../common/styled';
import { SearchInputWrapper } from '../../PendingFeeSummary/styled';
import { FilterFieldBox, FilterActionBox } from '../styled';
import { STUDENT_STATUS_OPTIONS } from '../../../constants/studentStatus';

const DEFAULT_OPTION = { label: 'All', value: 'All' };

const OutstandingFeeByClassReportFilters: React.FC<ReportFilterProps> = ({
  allClasses,
  loading,
  onSearch,
  onReset,
  componentsFilterConfig
}) => {
  const [session, setSession] = useState('');
  const [grade, setGrade] = useState('All');
  const [asOnDate, setAsOnDate] = useState<Dayjs | null>(dayjs());
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [studentStatus, setStudentStatus] = useState('All');
  const { toast, showToast, handleCloseToast } = useToast();

  useEffect(() => {
    setSession(defaultAcademicYear || '');
  }, []);

  const sessionOptions = [
    DEFAULT_OPTION,
    ...academicYears.map((y) => ({ label: y.label, value: y.value }))
  ];

  const gradeOptions = [
    DEFAULT_OPTION,
    ...(allClasses?.grades || []).map((g) => ({ label: g, value: g }))
  ];

  const showComponentsFilter =
    componentsFilterConfig?.showComponentsFilter === true;
  const componentOptions = (componentsFilterConfig?.components ?? []).map(
    (c: string) => ({ label: c, value: c })
  );

  const handleSessionChange = (
    e: React.ChangeEvent<{ value: unknown }> | any
  ) => {
    setSession(e.target.value as string);
    onReset();
  };

  const handleGradeChange = (
    e: React.ChangeEvent<{ value: unknown }> | any
  ) => {
    const selected = e.target.value as string;
    setGrade(selected);
    onReset();
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    const hasChanged =
      !newValue !== !asOnDate ||
      (newValue && asOnDate && !newValue.isSame(asOnDate, 'day'));
    setAsOnDate(newValue);
    if (hasChanged) onReset();
  };

  const handleViewClick = () => {
    if (!asOnDate) {
      showToast('Please select As On Date', SnackbarSeverityType.error);
      return;
    }
    onSearch({
      asOnDate: asOnDate.format('YYYY-MM-DD'),
      session,
      grade,
      division: 'All',
      components: selectedComponents.length > 0 ? selectedComponents : undefined,
      status: studentStatus === 'All' ? undefined : studentStatus
    });
  };

  const handleStudentStatusChange = (
    e: React.ChangeEvent<{ value: unknown }> | any
  ) => {
    const value = e.target.value as string;
    setStudentStatus(value);
    if (value === 'IN_ACTIVE') {
      setGrade('All');
    }
    onReset();
  };

  return (
    <>
      <PaperWrapper>
        <SearchInputWrapper>
          <FilterFieldBox>
            <DropdownSelect
              name='session'
              placeholder='Select Academic Year'
              value={session}
              onChange={handleSessionChange}
              label='Academic Year'
              options={sessionOptions}
            />
          </FilterFieldBox>
          <FilterFieldBox>
            <DropdownSelect
              name='grade'
              placeholder='Select Grade'
              value={grade}
              onChange={handleGradeChange}
              label='Grade'
              options={gradeOptions}
            />
          </FilterFieldBox>
          <FilterFieldBox>
            <DropdownSelect
              name='studentStatus'
              placeholder='Select Status'
              value={studentStatus}
              onChange={handleStudentStatusChange}
              label='Status'
              options={STUDENT_STATUS_OPTIONS}
            />
          </FilterFieldBox>
          <FilterFieldBox>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CustomDatePicker
                label='As On Date'
                value={asOnDate}
                onChange={handleDateChange}
                width='100%'
              />
            </LocalizationProvider>
          </FilterFieldBox>
          {showComponentsFilter && (
            <FilterFieldBox>
              <MultiSelectWithAll
                label='Component'
                required={false}
                options={componentOptions}
                selected={selectedComponents}
                onChange={(values) => {
                  const withoutAll = values.filter(
                    (v) => v !== DEFAULT_OPTION.value
                  );
                  setSelectedComponents(withoutAll);
                  onReset();
                }}
              />
            </FilterFieldBox>
          )}
          <FilterActionBox>
            <Button variant='contained' onClick={handleViewClick}>
              {loading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'View'
              )}
            </Button>
          </FilterActionBox>
        </SearchInputWrapper>
      </PaperWrapper>
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default OutstandingFeeByClassReportFilters;
